<template>
  <div class="tw:py-6 tw:px-5 tw:bg-white sign-in">
    <div class="sign-in__title">Welcome Back!</div>
    <p class="sign-in__subtitle">Enter your email and password</p>
    <form novalidate class="sign-in__form" @submit.prevent="handleForm">
      <div class="sign-in__input-wrapper">
        <img
          v-if="
            isEmailValid &&
            Object.keys(authErrors).length === 0 &&
            !isLoading &&
            inputData.email.length > 0
          "
          class="sign-in__email-check"
          src="../../assets/images/greenCheck.svg"
          alt=""
        />
        <img
          v-if="isEmailValid === false && inputData.email.length > 0"
          class="sign-in__email-check"
          src="../../assets/images/cross.svg"
          alt=""
          @click="clearInputAndErrors(['email'])"
        />
        <input
          id="email"
          v-model.trim="inputData.email"
          class="input"
          name="email"
          type="email"
          @input="handleInput"
          @change="handleInput"
          @keyup.delete="handleInput"
          @blur="activeInputName = null"
        />
        <label
          for="email"
          class="label"
          :class="{
            activeInput: inputData.email !== isEmptyInput || activeInputName === 'email',
          }"
        >
          Email
        </label>
        <span id="emailError" class="sign-in__error">
          <img
            class="sign-in__error-cross"
            src="../../assets/images/cross.svg"
            alt="cross"
            @click="clearInputAndErrors(['email'])"
          />
          <span class="sign-in__error-text">Required field</span>
        </span>
      </div>
      <div class="sign-in__input-wrapper">
        <img
          id="passwordShow"
          class="sign-in__toggle-show-password icon"
          src="../../assets/images/myScans/icon_action_hide.svg"
          alt=""
          @click="toggleShowPassword"
        />
        <img
          id="passwordHide"
          class="sign-in__toggle-show-password sign-in__toggle-show-password--bigger icon"
          src="../../assets/images/myScans/icon_action_show.svg"
          alt=""
          @click="toggleShowPassword"
        />
        <span id="authErrors" class="sign-in__error">
          <img
            class="sign-in__error-cross"
            src="../../assets/images/cross.svg"
            alt="cross"
            @click="clearInputAndErrors(['email', 'password'])"
          />
          <span class="sign-in__input-error">
            {{ authErrors.credentials || authErrors.dbConnecting }}
          </span>
        </span>
        <input
          id="password"
          v-model.trim="inputData.password"
          class="input"
          name="password"
          type="password"
          @input="checkKeyEvent"
          @blur="activeInputName = null"
        />
        <label
          for="password"
          class="label"
          :class="{
            activeInput: inputData.password !== isEmptyInput || activeInputName === 'password',
          }"
        >
          Password
        </label>
        <span id="passwordError" class="sign-in__error">
          <span class="sign-in__error-text">Required field</span>
        </span>
      </div>
      <div v-if="isOtpEnabled" class="sign-in__otp-warning">
        Please wait for your authentication app to generate a verification code, and then enter the
        code to continue.
      </div>
      <div v-if="isOtpEnabled" class="sign-in__input-wrapper">
        <img
          v-if="
            isOtpValid &&
            Object.keys(authErrors).length === 0 &&
            !isLoading &&
            inputData.otp.length > 0
          "
          class="sign-in__otp-check"
          src="../../assets/images/greenCheck.svg"
          alt=""
        />
        <img
          v-if="isOtpValid === false && inputData.otp.length > 0"
          class="sign-in__otp-check"
          src="../../assets/images/cross.svg"
          alt="cross"
          @click="clearInputAndErrors(['otp'])"
        />
        <span id="authOtpErrors" class="sign-in__error">
          <img
            class="sign-in__error-cross-otp"
            src="../../assets/images/cross.svg"
            alt="cross"
            @click="clearInputAndErrors(['otp'])"
          />
          <span class="sign-in__input-error">
            {{ authErrors.otp }}
          </span>
        </span>
        <input
          id="otp"
          v-model.trim="inputData.otp"
          class="input"
          name="otp"
          type="text"
          @input="handleInput"
          @change="handleInput"
          @keyup.delete="handleInput"
          @blur="activeInputName = null"
        />
        <label
          for="otp"
          class="label"
          :class="{
            activeInput: inputData.otp !== isEmptyInput || activeInputName === 'otp',
          }"
        >
          Secure Verification Code
        </label>
        <span id="otpError" class="sign-in__error">
          <img
            class="sign-in__error-cross"
            src="../../assets/images/cross.svg"
            alt="cross"
            @click="clearInputAndErrors(['otp'])"
          />
          <span class="sign-in__error-text">Required field</span>
        </span>
      </div>
      <div class="sign-in__toggle-wrapper">
        <b-form-checkbox
          id="rememberForTwoWeeks"
          v-model="rememberForTwoWeeks"
          name="rememberForTwoWeeks"
          value="true"
          unchecked-value="false"
        >
          Remember me for 2 weeks
        </b-form-checkbox>
        <div class="sign-in__forgot-password-wrapper">
          <b-nav-item to="/forgot-password" class="sign-in__text--link" @click="trackAuthForgotPasswordClicked">
            Forgot Password?
          </b-nav-item>
        </div>
      </div>
      <AuthActionButton
        full-width
        variant="primary"
        class="sign-in__submit-btn"
        :show-icon="false"
        button-type="submit"
        text="Sign In"
      />
      <div class="sign-in__entra-id-wrapper">
        <AuthActionButton
          v-if="isEntraIdSsoEnabled"
          full-width
          icon-type="microsoft"
          :loading="isEntraIdLoading"
          text="Sign in with Microsoft Entra ID"
          loading-text="Connecting to Microsoft..."
          @click="handleEntraIdLogin"
        />
        <AuthActionButton
          v-if="!isBsiPocEnv"
          full-width
          :loading="isGoogleLoading"
          text="Sign in with Google"
          loading-text="Connecting to Google..."
          @click="handleGoogleLogin"
        />
      </div>
      <div class="sign-in__link-to-login-wrapper">
        <p class="sign-in__register-text">Don't have an account?</p>
        <b-nav-item to="/sign-up/company" class="sign-in__link" @click="trackAuthCreateAccountClicked">Create now</b-nav-item>
      </div>
    </form>
    <div v-if="isLoading" class="sign-in__spinner">
      <Spinner />
    </div>
  </div>
</template>
<script>
import {
  checkFormInputsForCompleteness,
  checkFormInputsForErrors,
  raiseError,
  setItemsToLocalStorage,
  toggleShowPassword,
  validateEmail,
  hideErrors,
  validateOtp,
} from '@/services/functions';
import Spinner from '@/components/Spinner';
import AuthActionButton from '@/_core/shared/ui/AuthActionButton/AuthActionButton.vue';
import { mapGetters } from 'vuex';
import { store } from '@/store';
import {
  routeUserToPageByTypeAfterLogin,
  redirectUserToZendeskAfterLogin,
} from '@/services/authorization/routeUser';
import { AuthMethodEnum, AUTH_METHOD_STORAGE_KEY } from '@/utils/constants/common';
import {
  trackAuthSigninSubmitted,
  trackAuthSigninCompleted,
  trackAuthSigninFailed,
  trackAuthGoogleSigninClicked,
  trackAuthForgotPasswordClicked,
  trackAuthRememberMeToggled,
  trackAuthCreateAccountClicked,
} from '@/services/analytics';

const errorIds = [
  'emailError',
  'emailValidationError',
  'authErrors',
  'authOtpErrors',
  'passwordError',
  'otpError',
];
const BSI_POC_MARKER = 'indonesia';
export default {
  components: {
    Spinner,
    AuthActionButton,
  },
  data() {
    return {
      inputData: {
        email: '',
        password: '',
        otp: '',
      },
      rememberForTwoWeeks: false,
      isEmailValid: null,
      isOtpValid: null,
      isLoading: false,
      isOtpEnabled: false,
      isEntraIdLoading: false,
      isGoogleLoading: false,
      zendeskData: null,
      isEmptyInput: '',
      activeInputName: null,
      border: '1px solid #F0F0F0',
    };
  },
  computed: {
    ...mapGetters({
      authErrors: 'authentication/errors',
      userType: 'user/userType',
      isGetStartedCompleted: 'user/isGetStartedCompleted',
    }),
    isEntraIdSsoEnabled() {
      return 'SAML' in this.$route.query;
    },
    isBsiPocEnv() {
      const host = (window?.location?.hostname || '').toLowerCase();
      const baseUrl = (process.env.VUE_APP_BASE_URL || '').toLowerCase();
      const authBaseUrl = (process.env.VUE_APP_AUTH_BASE_URL || '').toLowerCase();
      const contentDomain = (process.env.VUE_APP_CONTENT_DOMAIN_NAME || '').toLowerCase();

      return (
        host.includes(BSI_POC_MARKER) ||
        baseUrl.includes(BSI_POC_MARKER) ||
        authBaseUrl.includes(BSI_POC_MARKER) ||
        contentDomain.includes(BSI_POC_MARKER)
      );
    },
  },
  watch: {
    rememberForTwoWeeks(val) {
      trackAuthRememberMeToggled(val === 'true');
    },
  },
  mounted() {
    if (this.$router.currentRoute.query) {
      this.zendeskData = this.$router.currentRoute.query;
    }
  },
  methods: {
    trackAuthForgotPasswordClicked,
    trackAuthCreateAccountClicked,
    clearErrors() {
      hideErrors(errorIds);
      store.commit('authentication/clearErrors');
      document.getElementById('password').style.border = this.border;
      document.getElementById('email').style.border = this.border;
      let otpElem = document.getElementById('otp');
      if (otpElem) {
        otpElem.style.border = this.border;
      }
    },
    clearInputAndErrors(dataIds) {
      if (Array.isArray(dataIds) && dataIds.length > 0) {
        dataIds.forEach((id) => {
          this.inputData[id] = '';
        });
      }
      this.clearErrors();
    },
    async handleForm() {
      this.clearErrors();
      trackAuthSigninSubmitted();

      const loginData = {
        email: this.inputData.email,
        password: this.inputData.password,
        otp: this.inputData.otp,
        remember_for_two_weeks: JSON.parse(this.rememberForTwoWeeks),
      };
      const requiredFields = this.isOtpEnabled
        ? this.inputData
        : {
            email: this.inputData.email,
            password: this.inputData.password,
          };

      checkFormInputsForErrors(requiredFields, 'sign-in__input--error');
      if (!checkFormInputsForCompleteness(requiredFields)) return;

      await this.sendAuthRequest(loginData);
    },
    isAuthError() {
      return this.authErrors && Object.keys(this.authErrors).length !== 0;
    },

    async sendAuthRequest(loginData) {
      this.isLoading = true;
      await this.$auth.login(loginData);
      // New auth flow: backend can respond with OTP_REQUIRED (401 + code),
      // which we map to authErrors.otp. In that case we should show OTP input.
      if (this.isAuthError() && this.authErrors.otp && !this.isOtpEnabled) {
        this.isOtpEnabled = true;
        this.$nextTick(() => {
          const otpElem = document.getElementById('otp');
          if (otpElem) otpElem.focus();
          // Show OTP error block (it's hidden by default via CSS).
          raiseError('otp', 'authOtpErrors', 'sign-in__input--error');
        });
        this.isLoading = false;
        return;
      }
      if (!this.isAuthError()) {
        trackAuthSigninCompleted(loginData.email);
        if (window.gtag) window.gtag('event', 'login', { method: 'oversecured' });
        if (typeof window.otag === 'function')
          window.otag({ event: 'LOGIN', email: loginData.email });
        await this.$store.dispatch('user/fetchUserInfo');
        setItemsToLocalStorage([
          ['userType', this.userType],
          [AUTH_METHOD_STORAGE_KEY, AuthMethodEnum.PASSWORD],
        ]);
        if (this.canRedirectToZendesk()) {
          await redirectUserToZendeskAfterLogin();
        } else {
          await routeUserToPageByTypeAfterLogin(
            this.userType,
            this.isGetStartedCompleted,
            this.$route.query.redirect
          );
        }
      } else if (this.isAuthError() && this.authErrors.link) {
        this.isLoading = false;
        await this.$router.push({
          name: 'ConfirmEmailLink',
          params: { email: this.inputData.email },
        });
        return;
      }
      if (this.isAuthError()) {
        trackAuthSigninFailed(this.authErrors.credentials || this.authErrors.dbConnecting || 'unknown');
      }
      this.isLoading = false;
    },
    canRedirectToZendesk() {
      return this.zendeskData && this.zendeskData.brand_id;
    },
    checkKeyEvent(event) {
      this.activeInputName = event.target.id;
    },
    handleInput(e) {
      this.checkKeyEvent(e);
      this.isEmailValid = validateEmail(this.inputData.email);
      this.isOtpValid = validateOtp(this.inputData.otp);

      this.clearErrors();
    },
    toggleShowPassword,

    handleEntraIdLogin() {
      if (this.isEntraIdLoading) return;
      this.isEntraIdLoading = true;

      setItemsToLocalStorage([[AUTH_METHOD_STORAGE_KEY, AuthMethodEnum.SSO]]);

      const ssoUrl = `${process.env.VUE_APP_AUTH_BASE_URL}/sso/entra-login`;
      console.log('[SSO] Redirecting to Keycloak/Entra ID:', ssoUrl);
      window.location.href = ssoUrl;
    },

    handleGoogleLogin() {
      if (this.isGoogleLoading) return;
      this.isGoogleLoading = true;
      trackAuthGoogleSigninClicked();

      setItemsToLocalStorage([[AUTH_METHOD_STORAGE_KEY, AuthMethodEnum.SSO]]);

      const ssoUrl = `${process.env.VUE_APP_AUTH_BASE_URL}/sso/google-login`;
      console.log('[SSO] Redirecting to Keycloak/Google:', ssoUrl);
      window.location.href = ssoUrl;
    },
  },
};
</script>

<style lang="scss">
@import '../../assets/css/fonts';

.maintenance-notice {
  background-color: #fff3cd;
  border-left: 4px solid #ff7a08;
  padding: 1rem 2rem;
  margin-bottom: 1rem;
  text-align: center;

  p {
    font-family: 'Aspekta', sans-serif;
    font-weight: 500;
    font-size: 16px;
    color: #856404;
    margin: 0;
  }
}

.sign-in {
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  &__spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }

  &__title {
    @include Aspekta-font(700, 60px, normal, #242424);
    font-size: calc(31px + (60 - 31) * calc((100vw - 320px) / (1920 - 320)));
    letter-spacing: 0em;
  }

  &__subtitle {
    @include Aspekta-font(500, 14px, 150%, #706969);
    margin: 12px 0 30px;
    text-align: center;
    max-width: 460px;
  }

  &__form {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 395px;
    margin-bottom: 40px;
  }

  &__label {
    width: 100%;
    margin-bottom: 15px;
    position: relative;
    font-family: Cabin;
    font-style: normal;
    font-size: 12px;
    line-height: 150%;
    color: #b2b9c4;

    &:nth-child(2) {
      margin-bottom: 10px;
    }
  }

  &__email-valid {
    position: absolute;
    top: 45px;
    right: 20px;
  }

  &__toggle-show-password {
    position: absolute;
    right: 11px;
    top: 6px;
    width: 30px;
    cursor: pointer;
  }

  &__otp-valid {
    position: absolute;
    top: 45px;
    right: 20px;
  }

  &__input {
    &--error {
      border: 1px solid #fb8d62;
      border-radius: 5px;
    }
  }

  &__input-wrapper {
    position: relative;
    width: 100%;

    .icon {
      margin: 4px;
      width: 22px;
      height: 22px;
    }
  }

  &__otp-warning {
    margin-bottom: 25px;
    text-align: center;
  }

  &__input-error {
    position: absolute;
    padding-top: 13%;
    right: 0;
    font-family: Cabin, serif;
    font-weight: normal;
    font-size: 12px;
    color: #fb8d62;
  }

  &__email-check {
    position: absolute;
    top: 16px;
    right: 20px;
  }

  &__otp-check {
    position: absolute;
    top: 16px;
    right: 20px;
  }

  &__toggle-wrapper {
    @include Aspekta-font(500, 14px, normal, #706969);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 16px;

    label.custom-control-label {
      padding-top: 2px;
    }

    .custom-control-label::after {
      background-color: #ebf2fd;
      border-radius: 4px;
      border: 1px solid #3180f6;
    }

    .custom-control-input:checked ~ .custom-control-label::after {
      background-color: #3180f6;
    }
  }

  &__link {
    display: inline-block;
    @include Aspekta-font(500, 14px, normal, transparent);
    align-items: center;

    a {
      @include Aspekta-font(500, 14px, normal, #ffb400);
      font-size: 14px !important;
      font-weight: 500;
      line-height: normal;

      &:hover {
        text-decoration: underline;
        color: #ff7a08;
      }
    }
  }

  &__link-to-login-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__register-text {
    @include Aspekta-font(500, 14px, normal, #706969);
    margin: 0;
  }

  &__forgot-password-wrapper {
    margin: 0;
    width: auto;
    display: flex;
    justify-content: flex-end;
  }

  &__text {
    font-weight: 500;
    font-size: 18px;
    color: #706969;

    &--link {
      a {
        @include Aspekta-font(500, 14px, normal, #ffb400);
        font-size: 14px !important;
        font-weight: 500;
        line-height: normal;
        margin: 0 !important;

        &:hover {
          color: #ff7a08;
          text-decoration: underline;
        }
      }
    }
  }

  &__submit-btn {
    margin: 0;
  }

  &__entra-id-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin: 12px 0 16px;
    gap: 12px;
  }

  &__sso-note {
    margin-top: 12px;
    font-size: 13px;
    color: #666;
    text-align: center;
    max-width: 400px;
    line-height: 1.5;
  }

  &__error {
    width: 100%;
    max-width: 831px;
    position: relative;
  }

  &__error-cross {
    position: absolute;
    top: -56px;
    right: 20px;
  }

  &__error-cross-otp {
    position: absolute;
    top: 16px;
    right: 20px;
  }

  &__error-text {
    @include Aspekta-font(500, 10px, 150%, #fb8d62);
    position: absolute;
    top: -30px;
    left: 3px;
    margin: 0;
  }

  &__email-check {
    cursor: pointer;
    top: 18px;
    right: 20px;
  }
}

#passwordError,
#emailError,
#otpError,
#authErrors,
#authOtpErrors {
  display: none;
}
</style>
